/** @jsx jsx */
import {jsx} from 'theme-ui'
import useSWR from 'swr'
import env from 'lib/env'
import {DonationPurpose} from './DonationBox/shared'
import Table from './Table'
import {Fragment} from 'react'
import Loader from './Loader'
import CancelSubscriptionButton from './CancelSubscriptionButton'
import useUser from 'hooks/useUser'

type Subscription = {
  amount: number
  charity_organization: DonationPurpose
  created_at: string
  currency: 'usd'
  email: string
  metadata: {}
  payment_gateway: 'STRIPE' | 'PAYPAL'
  subscription_id: string
  subscription_status: 'ENABLED'
}
type RowItem = {
  index: number
  date: Date
  purpose: string
  amount: number
  status: string
  cancel: React.ReactElement | null
  payment_gateway: 'STRIPE' | 'PAYPAL'
}

const columns: {
  id: keyof RowItem
  label: string
  minWidth: number
  align?: 'left' | 'right' | 'center'
  format?: (source: any) => string
}[] = [
  {
    id: 'date',
    label: 'Creation Date',
    minWidth: 100,
    format: (date: Date) => date.toLocaleDateString(),
  },
  {
    id: 'purpose',
    label: 'Cause',
    minWidth: 140,
  },
  {
    id: 'payment_gateway',
    label: 'Method',
    minWidth: 120,
    // TODO use translate
    format: (payment_gateway: 'STRIPE' | 'PAYPAL') =>
      payment_gateway === 'STRIPE' ? 'Credit Card' : 'PayPal',
  },
  {
    id: 'amount',
    label: 'Amount (USD)',
    minWidth: 100,
    align: 'center',
    format: (amount: number) => `$${amount}`,
  },
  {
    id: 'status',
    label: 'Status',
    minWidth: 100,
    format: (status: string) => {
      if (status === 'CREATED') return 'PENDING'
      return status
    },
  },
  {id: 'cancel', label: '', minWidth: 100},
]

type Props = {
  errorTitle: string
  errorText: string
}

const AccountSubscriptions: React.FC<Props> = ({errorTitle, errorText}) => {
  const {token} = useUser()
  const {data, error, revalidate} = useSWR<{
    subscriptions: Subscription[]
  }>(`${env.GATSBY_DONATION_API}auth/subscriptions/`)

  if (error) {
    return (
      <Fragment>
        <h1>{errorTitle}</h1>
        <p>{errorText}</p>
      </Fragment>
    )
  }
  if (!data) {
    return (
      <div
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: 50,
        }}
      >
        <Loader />
      </div>
    )
  }
  const items: RowItem[] = data.subscriptions.map((s, i) => {
    return {
      index: i,
      date: new Date(s.created_at),
      purpose: s.charity_organization,
      amount: s.amount,
      status: s.subscription_status,
      payment_gateway: s.payment_gateway,
      cancel:
        s.subscription_status === 'ENABLED' ? (
          <CancelSubscriptionButton
            callback={revalidate}
            subscriptionId={s.subscription_id}
            token={token}
            paymentGateway={s.payment_gateway}
          />
        ) : null,
    }
  })

  return <Table items={items} columns={columns} />
}

export default AccountSubscriptions
